import { LIBRARY_FREE_PERCENTAGE } from '@/constants/library-free-percentage';
import type { PricingOption } from '@/hooks/api/usePricingOptions';
import type { FAQItem, FAQs } from '@/types/referAndEarn';
import type { ValidCurrency } from '@/types/plans';
import type { Settings } from '@/types/settings';
import { isBusinessPlanName } from '@/lib/pricing/businessPlans';
import { getLocaleForCurrency } from '@/utils/getLocaleForCurrency';
import type React from 'react';

export interface PricingCardBulletPoint {
  content: string | React.ReactElement;
  tooltip?: string;
}
export const PRICING_FAQS: FAQs = [
  {
    question: 'Where can I use Uppbeat music & other assets?',
    answer: `Uppbeat assets are safe for:

- **Online content:** Enhance your YouTube videos, social media posts, live streams, and website content.
- **Monetization:** Our assets are safe for monetized, sponsored, or affiliated content. This includes pay-to-access platforms like Patreon.
- **Commercial content:** For paid advertising or client projects, freelancers and solo professionals need Uppbeat Pro, and companies, brands, and agencies need Uppbeat Business.

**Need more coverage?** Broadcast, radio, cinema, streaming and other bespoke and other high-scale usages sit outside our standard plans — get in touch about our custom licensing option.`,
    showCustomLicensingButton: true,
  },
  {
    question: 'Which plan is best for me?',
    answer: `The best plan for you depends on your needs, and the kind of team you work in.

1. **Which asset types do you need?**

Taking out a subscription gives you **unlimited downloads** and access to **premium assets**, depending on the type of subscription you take out:
- **Music and sound effects** are available on **all paid subscription plans**.
- **Stock video, motion graphics and LUTs** are available on **Uppbeat Creator** and above.

2. **Are you part of a team or organization?**

This is the biggest factor in which plan you need:
- Individual creators making content for their own channels are covered by all subscription plans.
- Freelancers and solo professionals working with clients are covered by Uppbeat Pro.
- Companies, brands, agencies, teams and organizations of any size need Uppbeat Business.

3. **What kind of content do you make?**

Some kinds of content, such as paid advertising or client work, also require extended licenses:
- Content for your own personal channel is covered by all subscription plans.
- If you're a freelancer working with clients or running paid ads, you need Uppbeat Pro.
- If you're a company, brand, or agency working with clients or running paid ads, you need Uppbeat Business.`,
  },
  {
    question: 'Can I use assets for broadcast, cinema, or streaming services?',
    answer:
      'Broadcast, radio, cinema, streaming or other bespoke/high-scale usage needs our custom licensing option — get in touch and our dedicated team will be happy to help.',
    showCustomLicensingButton: true,
  },
  {
    question: 'What file formats/download qualities are available?',
    answer: `By default, audio assets (music and sound effects) are available in mp3 format, and stock footage is available in 720p. Taking out a subscription gives you higher quality formats, depending on the type of subscription you take out:

   Audio files (music and sound effects):
   - **mp3 downloads** are available **for all users**.
   - **Uncompressed WAV files** are available on **Uppbeat Pro** and **Uppbeat Business**.

   Video files (stock video):
   - **720p** assets are available **for all users**.
   - **1080p** assets are available on **Uppbeat Creator** and above.
   - **4K** assets are available on **Uppbeat Pro** and **Uppbeat Business**.`,
  },
  {
    question: 'Is the music copyright-free?',
    answer: `Yes! Uppbeat's music is copyright-free.

To avoid claims, you simply add an Uppbeat Credit to your video description.

With a subscription, you can also safelist your channels for complete convenience and peace of mind, without having to use Uppbeat Credits.`,
  },
  {
    question: 'How do artists get paid?',
    answer: `At Uppbeat, we're proud to champion creativity and stand behind the creators who make it all possible.

That's why we operate on a **revenue sharing model** where the majority of the money from your subscription and other sources goes directly to our contributors.

**Every time you download an asset from Uppbeat, the contributor earns a payment**. This happens whether you're paying for Uppbeat or not; we make sure that every use of their work gets compensated.

**We also believe that artists should retain full ownership of their work**. Unlike many other platforms that buy out content rights for a one-time fee, Uppbeat allows contributors to earn ongoing revenue from their work while keeping their rights.

By using Uppbeat, you're not just getting high-quality, human-made creative assets: You're directly supporting independent musicians, sound designers, videographers and motion graphic designers.`,
  },
];

const formatPlanPrice = (amount: number, currency: Settings['currency']) => {
  const code = (currency?.code || 'USD') as ValidCurrency;
  const number = new Intl.NumberFormat(getLocaleForCurrency(code), {
    // Whole amounts stay whole (matches the pricing cards); anything else gets two decimals.
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
  // "$" alone is ambiguous for CAD, same as the pricing cards.
  const suffix = code === 'CAD' ? ' CAD' : '';

  return `${currency?.symbol_left ?? '$'}${number}${suffix}`;
};

const markdownPricingTable = (
  pricingPlans: PricingOption[],
  currency: Settings['currency'],
  taxNote: string
) => {
  const code = currency?.code?.toLowerCase() as keyof PricingOption['pricing'];
  const rows = pricingPlans
    // Free is described underneath the table.
    .filter((plan) => plan.pricing[code] && plan.title !== 'Free')
    .map(({ title, pricing }) => {
      const { monthly, yearly } = pricing[code];
      const isBusiness = isBusinessPlanName(title);
      // Business is annual-only and quoted excluding tax.
      const monthlyPrice = isBusiness
        ? '_N/A_'
        : formatPlanPrice(monthly, currency);
      const vatNote = isBusiness ? ` ${taxNote}` : '';
      return `| ${title} | ${monthlyPrice} | ${formatPlanPrice(yearly, currency)}${vatNote} _(Equivalent to ${formatPlanPrice(yearly / 12, currency)} per month)_ |`;
    });

  return [
    '| Plan | Monthly Billing | Yearly Billing |',
    '| -------- | ------- | ------- |',
    ...rows,
  ].join('\n');
};

export const pricingTableFaqFAQItem = (
  pricingPlans: PricingOption[],
  currency: Settings['currency'],
  taxNote: string
): FAQItem => ({
  question: 'How much does Uppbeat cost?',
  answer: `A full breakdown of our subscription plans and their features can be found at the top of this page, but here's a quick summary:

${markdownPricingTable(pricingPlans, currency, taxNote)}

If you're not ready to decide, then you can start with a free account. You'll get access to ${LIBRARY_FREE_PERCENTAGE} of our catalog, and 3 free downloads upfront - topped up by 1 each month.`,
});
